// @ts-nocheck
import { isEmpty } from 'lodash';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { MapLegend, useReportingMap } from 'sonata-platform-components';
import {
  CartographyData,
  LocationsData,
  Map as MapComponent,
  ScreensStoresData
} from 'sonata-platform-components/lib/types/types/reportingMap.types';
import { EventLegend } from 'sonata-react-components/lib/types/src_ts/components/MapLegend/mapLegend.types';
import { DEFAULT_COLOR_PALETTE_MAP, mapPrefs } from 'sonata-static-storage/lib/samples/mapPreferences.samples';

import { formatNumber, getLayerColors, HEATMAP_COLORS, REPORTING_COLORS } from '@/helpers/reporting.helpers';
import { getBaseCurrency } from '@/store/selectors/baseCurrency.selectors';
import { getCurrency } from '@/store/selectors/reportingAdvancedFilter.selectors';

const getEventType = (event: string): EventLegend['type'] => {
  const RANGE_TYPES = ['ctr', 'vtr', 'atr', 'etr', 'pvtr', 'hctr', 'hvtr'];

  return RANGE_TYPES.includes(event) ? 'range' : 'simple';
};

type Props = {
  mapId: string;
  metricLabel: string;
};

export function Legends({ mapId, metricLabel }: Props) {
  const { formatMessage } = useIntl();
  const { getMap, getMapCartographyData, getMapLocationsData, getMapScreensData, getMapStoresData, setMapLegend } =
    useReportingMap();
  const cartographyData: CartographyData[] = getMapCartographyData({ id: mapId });
  const storesData: ScreensStoresData[] = getMapStoresData({ id: mapId });
  const locationsData: LocationsData[] = getMapLocationsData({ id: mapId });
  const screensData: ScreensStoresData[] = getMapScreensData({ id: mapId });
  // @ts-ignore
  const currentCurrency = useSelector(store => getCurrency(store, 'general'));
  // @ts-ignore
  const currencyInfo = useSelector(store => getBaseCurrency(store, currentCurrency));

  const { legend: legendItems, type: mapType } = getMap({ id: mapId }) as MapComponent;

  const onVisibleChange = ({ event, visible }: { event: string; visible: boolean }) => {
    const newLegendState = legendItems.map(item =>
      item.event === event
        ? {
            event,
            visible: !visible
          }
        : item
    );
    setMapLegend({
      id: mapId,
      value: newLegendState
    });
  };

  const getMaxMinValues = (): EventLegend['maxMinValues'] => {
    let data: CartographyData[] | LocationsData[] | ScreensStoresData[] | undefined;
    switch (mapType) {
      case 'cartography':
        data = cartographyData;
        break;
      case 'locations':
        data = locationsData;
        break;
      case 'screens':
        data = screensData;
        break;
      case 'stores':
        data = storesData;
        break;
      default:
        break;
    }
    if (isEmpty(data) || ['single', 'heatmap'].includes(mapType)) {
      return {};
    }
    const metrics = data!
      // @ts-ignore
      .map(({ metric }) => metric)
      .filter(d => d !== undefined)
      .sort((a, b) => a - b);
    const max = formatNumber(metricLabel, metrics[metrics.length - 1], currencyInfo, true);
    const min = formatNumber(metricLabel, metrics.length === 1 ? 0 : metrics[0], currencyInfo, true);
    return {
      max,
      min
    };
  };

  const mapLegendData = legendItems.reduce((acc: EventLegend[], actualItem) => {
    const { event, ...rest } = actualItem;
    const type = getEventType(event);
    const eventKey: keyof typeof REPORTING_COLORS = event as any; // todo trampas al solitario
    const colors: EventLegend['colors'] =
      type === 'range'
        ? getLayerColors([REPORTING_COLORS[eventKey]]).map(layerColor => layerColor.color)
        : [REPORTING_COLORS[eventKey] || mapPrefs.events.colors[eventKey]];

    if (['cartography', 'heatmap', 'stores', 'locations', 'screens'].includes(mapType)) {
      acc.push({
        ...rest,
        colors: mapType === 'heatmap' ? HEATMAP_COLORS : DEFAULT_COLOR_PALETTE_MAP,
        event,
        label: formatMessage({ id: `reporting.metrics.${event}` }),
        mapType,
        maxMinValues: getMaxMinValues(),
        onClick: onVisibleChange,
        type: 'range'
      });
    } else {
      acc.push({
        ...rest,
        colors,
        event,
        label: formatMessage({ id: `reporting.metrics.${event}` }),
        mapType,
        onClick: onVisibleChange,
        type
      });
    }

    return acc;
  }, []);

  const legendKey = mapLegendData.map(option => option.event).join('-');

  return <MapLegend data={mapLegendData} key={legendKey} />;
}
